import { useContext, useEffect, useState } from "react";
import { Plus } from "lucide-react";

// Contexts
import { ThemeContext } from "../theme";
import { LanguageContext } from "../LanguageContext";

// Constants / translations
import { text } from "./constants/translations";

// Hooks
import useResponsive from "./hooks/useResponsive";
import useEscalations from "./hooks/useEscalations";
import useStyles from "./styles/useStyles";

// Components
import Alert from "./components/Alert";
import StatusBadge from "./components/StatusBadge";
import PriorityBadge from "./components/PriorityBadge";
import CreateEscalationModal from "./components/CreateEscalationModal";
import MyEscalationsTable from "./components/MyEscalationsTable";
import GroupAssignedEscalation from "./components/GroupAssignedEscalation";

const EscalationComplaints = () => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);

  // ── Responsive ───────────────────────────────────────────
  const { screenSize, spacing, fonts } = useResponsive();

  // ── Styles ───────────────────────────────────────────────
  const styles = useStyles({ theme, screenSize, spacing, fonts, language });

  // ── Data / state ─────────────────────────────────────────
  const {
    escalations,
    assignedEscalations,
    groups,
    selectedGroup,
    loading,
    error,
    success,
    stats,
    setSelectedGroup,
    setError,
    setSuccess,
    fetchMyEscalations,
    fetchGroups,
    fetchEscalations,
    handleSubmitEscalation,
    handleUpdateStatus,
  } = useEscalations();

  // ── Local UI state ────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("my");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [hoveredTab, setHoveredTab] = useState(null);

  // ── Shorthand translations ────────────────────────────────
  const t = text[language];

  // ── Effects ───────────────────────────────────────────────
  useEffect(() => {
    if (activeTab === "my") fetchMyEscalations();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "assigned") fetchGroups();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "assigned" && groups.length > 0) fetchEscalations();
  }, [selectedGroup, groups, activeTab]);

  // ── Handlers ─────────────────────────────────────────────
  const handleCreate = () => setShowCreateModal(true);

  const handleSubmit = async (data) => {
    await handleSubmitEscalation(data);
    setShowCreateModal(false);
  };

  // ── Badge wrappers (pass theme/screen/fonts automatically) ─
  const BoundStatusBadge = ({ status }) => (
    <StatusBadge status={status} t={t} screenSize={screenSize} fonts={fonts} />
  );

  const BoundPriorityBadge = ({ priority }) => (
    <PriorityBadge
      priority={priority}
      t={t}
      screenSize={screenSize}
      fonts={fonts}
    />
  );

  // ── Render ────────────────────────────────────────────────
  return (
    <div style={styles.container}>
      {/* Alerts */}
      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => setError("")}
          screenSize={screenSize}
          fonts={fonts}
        />
      )}
      {success && (
        <Alert
          type="success"
          message={success}
          onClose={() => setSuccess("")}
          screenSize={screenSize}
          fonts={fonts}
        />
      )}

      {/* Header */}
      <div style={styles.card1}>
        <div style={styles.headerContent}>
          <div>
            <h1 style={styles.title}>{t.title}</h1>
          </div>
          <button style={styles.createBtn} onClick={handleCreate}>
            <Plus size={screenSize === "xs" ? 18 : 20} />
            {t.createEscalation}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabContainer}>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === "my" ? styles.activeTab : {}),
            ...(hoveredTab === "my" && activeTab !== "my" ? {
              background: theme === "dark" 
                ? "rgba(255,255,255,0.08)" 
                : "rgba(0,0,0,0.04)",
              color: theme === "dark" 
                ? "rgba(255,255,255,0.75)" 
                : "rgba(26,26,26,0.8)",
              transform: "translateY(-2px)",
            } : {}),
          }}
          onClick={() => setActiveTab("my")}
          // onMouseEnter={() => setHoveredTab("my")}
          // onMouseLeave={() => setHoveredTab(null)}
        >
          {t.myEscalations}
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === "assigned" ? styles.activeTab : {}),
            ...(hoveredTab === "assigned" && activeTab !== "assigned" ? {
              background: theme === "dark" 
                ? "rgba(255,255,255,0.08)" 
                : "rgba(0,0,0,0.04)",
              color: theme === "dark" 
                ? "rgba(255,255,255,0.75)" 
                : "rgba(26,26,26,0.8)",
              transform: "translateY(-2px)",
            } : {}),
          }}
          onClick={() => setActiveTab("assigned")}
          // onMouseEnter={() => setHoveredTab("assigned")}
          // onMouseLeave={() => setHoveredTab(null)}
        >
          {t.assignedEscalations}
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === "my" && (
        <MyEscalationsTable
          loading={loading}
          escalations={escalations}
          stats={stats}
          screenSize={screenSize}
          language={language}
          t={t}
          theme={theme}
          styles={styles}
          fonts={fonts}
          StatusBadge={BoundStatusBadge}
        />
      )}

      {activeTab === "assigned" && (
        <GroupAssignedEscalation
          assignedEscalations={assignedEscalations}
          groups={groups}
          selectedGroup={selectedGroup}
          setSelectedGroup={setSelectedGroup}
          loading={loading}
          onUpdateStatus={handleUpdateStatus}
          theme={theme}
          screenSize={screenSize}
          language={language}
          t={t}
          styles={styles}
          fonts={fonts}
          spacing={spacing}
          StatusBadge={BoundStatusBadge}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateEscalationModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleSubmit}
          loading={loading}
          theme={theme}
          screenSize={screenSize}
          styles={styles}
          language={language}
          t={t}
        />
      )}
    </div>
  );
};

export default EscalationComplaints;